import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { FollowUser } from "./follows";

// ===== 型定義 =====

/**
 * Role が受け取れるポジション。BE 側の `accept_position` と対応。
 *
 * 値域は仕様書「メンバーシップポジション API 表記統一」(2026-05-09) に従い、
 * UPPERCASE 3 値で統一されている:
 * - `PRODUCER`   : 責任者プロデューサー（再委託可・顧客コミュニケーション権限を内包）
 * - `DIRECTOR`   : プロデューサー（再委託可・顧客コミュニケーション権限を内包）
 * - `SPECIALIST` : スペシャリスト系
 *
 * 「変更なし」は `null`（書き込み時は `''` でも等価）。
 */
export type RoleAcceptPosition = "PRODUCER" | "DIRECTOR" | "SPECIALIST";

/** Role 一覧 (`GET /api/roles/`) で返るタグ参照: id のみ */
export interface RoleTagRef {
  id: number;
  name?: string; // 詳細・タグ一覧 API のときのみ
  order?: number;
}

/** ロールタグ (`GET /api/roles/tags/`) */
export interface RoleTag {
  id: number;
  name: string;
  order: number;
}

/**
 * 候補リスト共通形 (`deliverable_format_options` / `special_provisions_options`)
 *
 * BE 仕様変更により単純な文字列配列に統一された。
 * **配列順がそのまま表示順**で、**先頭がデフォルト選択**として扱う。
 */
export type RoleOptionItem = string;

/**
 * 対価行
 * `unit` は数量の単位（`"式"` / `"本"` / `"時間"` / `"ヶ月"` 等、空文字許容）。
 * 配列順がそのまま表示順。プリセット名のような階層は廃止された。
 */
export interface CompensationLine {
  label: string;
  unit_price: string; // BE は文字列で返してくる
  quantity: string;
  unit: string;
}

/** ロール一覧アイテム（軽量） */
export interface RoleListItem {
  id: number;
  role_name_jp: string;
  role_name_en: string;
  credit_title_jp: string;
  credit_title_en: string;
  /** ロールの説明文（カード表示等に利用） */
  description: string;
  /** タグ id の配列（タグ名は `useGetRoleTagsQuery` を併用して解決する） */
  tags: number[];
  default_total_amount: string;
  /** 受託者ポジション（PRODUCER / DIRECTOR は再委託・顧客コミュニケーション権限を内包） */
  accept_position: RoleAcceptPosition;
  /** 受託者から再委託可とするかのデフォルト */
  allow_subcontracting: boolean;
}

/**
 * ロール詳細。仕様書 §3 に準拠して `tags`（int 配列、PATCH の payload としても受け付け）と
 * `is_active`（writer による有効/無効フラグ）を含む。
 */
export interface RoleDetail {
  id: number;
  role_name_jp: string;
  role_name_en: string;
  credit_title_jp: string;
  credit_title_en: string;
  description: string;
  accept_position: RoleAcceptPosition;
  allow_subcontracting: boolean;
  deadline_offset_days: number;
  deadline_time: string;
  valid_until_offset_days: number;
  valid_until_time: string;
  contract_start_offset_days: number | null;
  contract_end_offset_days: number | null;
  scope: string;
  initial_message: string;
  delivery_location: string;
  delivery_method: string;
  deliverable_format_options: RoleOptionItem[];
  special_provisions_options: RoleOptionItem[];
  compensation_lines: CompensationLine[];
  default_total_amount: string;
  tags: number[];
  is_active: boolean;
}

/**
 * Role の作成 / 更新に使うペイロード。仕様書 §4 / §4.1 に準拠。
 * すべて任意（PATCH 部分更新）。POST は `role_name_jp` のみ実質必須で、
 * 他は Role モデルの default 値で初期化される。
 *
 * `tags` は **指定 ID で全置換**（`[]` で全削除）、未指定なら現状維持。
 */
export interface RoleWritePayload {
  role_name_jp?: string;
  role_name_en?: string;
  credit_title_jp?: string;
  credit_title_en?: string;
  description?: string;
  accept_position?: RoleAcceptPosition;
  allow_subcontracting?: boolean;
  deadline_offset_days?: number;
  deadline_time?: string;
  valid_until_offset_days?: number;
  valid_until_time?: string;
  contract_start_offset_days?: number | null;
  contract_end_offset_days?: number | null;
  scope?: string;
  initial_message?: string;
  delivery_location?: string;
  delivery_method?: string;
  deliverable_format_options?: RoleOptionItem[];
  special_provisions_options?: RoleOptionItem[];
  compensation_lines?: CompensationLine[];
  tags?: number[];
  is_active?: boolean;
}

/** POST /api/roles/ では `role_name_jp` のみ実質必須。 */
export type CreateRolePayload = RoleWritePayload & { role_name_jp: string };

/** PATCH /api/roles/<id>/ は完全な部分更新。 */
export type UpdateRolePayload = RoleWritePayload;

/** offer-prefill レスポンスの `data` */
export interface RoleOfferPrefill {
  role: {
    id: number;
    role_name_jp: string;
    role_name_en: string;
    credit_title_jp: string;
    credit_title_en: string;
    description: string;
    /** prefill API ではタグ名の配列を返す仕様 */
    tags: string[];
    default_total_amount: string;
  };
  defaults: {
    accept_position: RoleAcceptPosition;
    allow_subcontracting: boolean;
    deadline_offset_days: number;
    deadline_time: string;
    valid_until_offset_days: number;
    valid_until_time: string;
    contract_start_offset_days: number | null;
    contract_end_offset_days: number | null;
    scope: string;
    initial_message: string;
    delivery_location: string;
    delivery_method: string;
  };
  deliverable_format_options: RoleOptionItem[];
  special_provisions_options: RoleOptionItem[];
  compensation_lines: CompensationLine[];
}

// ===== レスポンス共通形 =====

export interface RolesListResponse {
  status: string;
  data: {
    roles: RoleListItem[];
    count: number;
    limit: number;
    offset: number;
    has_next: boolean;
  };
}

export interface RoleTagsListResponse {
  status: string;
  data: {
    tags: RoleTag[];
    count: number;
  };
}

export interface RoleDetailResponse {
  status: string;
  data: {
    role: RoleDetail;
  };
}

export interface RoleOfferPrefillResponse {
  status: string;
  data: RoleOfferPrefill;
}

/** 自分のアクティブロール一覧 (`GET /api/me/roles`) */
export interface MyRolesResponse {
  status: string;
  data: {
    roles: RoleListItem[];
    count: number;
  };
}

/** 自分のロールのアクティブ化 / 解除レスポンス */
export interface MyRoleToggleResponse {
  status: string;
  data: {
    role_id: number;
    is_active: boolean;
  };
  message?: string;
}

/**
 * 他ユーザーへのロール割当 / 解除レスポンス（仕様書 §6.5）。
 * 自分のセルフ操作（`MyRoleToggleResponse`）と異なり `user_id` を含む。
 */
export interface AssignmentToggleResponse {
  status: string;
  data: {
    user_id: number;
    role_id: number;
    is_active: boolean;
  };
  message?: string;
}

/** ロール削除レスポンス（仕様書 §4.2） */
export interface RoleDeleteResponse {
  status: string;
  data: { role_id: number };
  message?: string;
}

/** ロールタグ単体レスポンス（POST / PATCH 共通、仕様書 §5.1.1 / §5.1.2） */
export interface RoleTagSingleResponse {
  status: string;
  data: { tag: RoleTag };
  message?: string;
}

/** ロールタグ削除レスポンス（仕様書 §5.1.3） */
export interface RoleTagDeleteResponse {
  status: string;
  data: { tag_id: number };
  message?: string;
}

/**
 * 指定ロール×ユーザー一覧 (`GET /api/roles/<role_id>/users`) のユーザー要素。
 * `FollowUser` (フォロー / フォロワー API と共通の最小ユーザー形。`vanity_slug` を含む)
 * を継承しつつ、ロール×ユーザー API 固有の `accepts_open_offers` を加えたもの。
 *
 * `accepts_open_offers` は対応する `Creator` が無い場合 `false`。
 */
export interface RoleUserItem extends FollowUser {
  accepts_open_offers: boolean;
}

export interface RoleUsersResponse {
  status: string;
  data: {
    users: RoleUserItem[];
    count: number;
    limit: number;
    offset: number;
    has_next: boolean;
  };
}

// ===== クエリ引数 =====

export interface ListRolesParams {
  tag_id?: number;
  limit?: number;
  offset?: number;
}

/**
 * オブジェクトの値を URLSearchParams 形式に整形して `?key=...` を返す共通ヘルパー。
 * `undefined` の項目は除外し、`true` は `"1"` に正規化する。
 * すべて空であれば空文字列を返すので、URL 末尾の `?` 付与有無を呼び出し側で
 * 気にしなくてよい。
 */
const toQueryString = (
  params: Record<string, string | number | boolean | undefined>,
): string => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === false) continue;
    out[key] = value === true ? "1" : String(value);
  }
  const qs = new URLSearchParams(out).toString();
  return qs ? `?${qs}` : "";
};

const buildRolesQueryString = ({ tag_id, limit, offset }: ListRolesParams): string =>
  toQueryString({ tag_id, limit, offset });

/**
 * `GET /api/roles/<role_id>/users` のクエリ。
 * 指定したパラメータだけが AND 結合される（仕様書 §6.4 参照）。
 */
export interface ListRoleUsersParams {
  role_id: number;
  /** 認証ユーザーがフォローしている対象に絞る */
  following?: boolean;
  /** Creator.accepts_open_offers=True のユーザーに絞る */
  accepting_offers?: boolean;
  limit?: number;
  offset?: number;
}

const buildRoleUsersQueryString = ({
  following,
  accepting_offers,
  limit,
  offset,
}: ListRoleUsersParams): string =>
  toQueryString({ following, accepting_offers, limit, offset });

// ===== API スライス =====

export const rolesApi = createApi({
  reducerPath: "rolesApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: [
    "Roles",
    "RoleTags",
    "RoleDetail",
    "MyRoles",
    "UserRoles",
    "RoleUsers",
  ],
  endpoints: (builder) => ({
    getRoles: builder.query<RolesListResponse, ListRolesParams>({
      query: (params) => `/api/roles/${buildRolesQueryString(params)}`,
      providesTags: ["Roles"],
    }),

    getRoleTags: builder.query<RoleTagsListResponse, void>({
      query: () => `/api/roles/tags/`,
      providesTags: ["RoleTags"],
    }),

    getRoleDetail: builder.query<RoleDetailResponse, number>({
      query: (roleId) => `/api/roles/${roleId}/`,
      providesTags: (_result, _error, roleId) => [
        { type: "RoleDetail", id: roleId },
      ],
    }),

    getRoleOfferPrefill: builder.query<RoleOfferPrefillResponse, number>({
      query: (roleId) => `/api/roles/${roleId}/offer-prefill/`,
      providesTags: (_result, _error, roleId) => [
        { type: "RoleDetail", id: roleId },
      ],
    }),

    /**
     * ロール新規作成（仕様書 §4.1）。writer 権限のみ。
     */
    createRole: builder.mutation<RoleDetailResponse, CreateRolePayload>({
      query: (body) => ({
        url: `/api/roles/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Roles"],
    }),

    /**
     * ロール部分更新（仕様書 §4）。`tags` は指定で全置換、未指定で現状維持。
     */
    updateRole: builder.mutation<
      RoleDetailResponse,
      { roleId: number; payload: UpdateRolePayload }
    >({
      query: ({ roleId, payload }) => ({
        url: `/api/roles/${roleId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (_result, _error, { roleId }) => [
        "Roles",
        { type: "RoleDetail", id: roleId },
      ],
    }),

    /**
     * ロール物理削除（仕様書 §4.2）。`CreatorRole` / `RoleTagAssignment` も
     * cascade で消えるため、関連タグの集合も無効化しておく。
     */
    deleteRole: builder.mutation<RoleDeleteResponse, number>({
      query: (roleId) => ({
        url: `/api/roles/${roleId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Roles", "MyRoles", "RoleUsers", "UserRoles", "RoleTags"],
    }),

    /**
     * ロールタグ新規作成（仕様書 §5.1.1）。`order` 省略時は末尾追加。
     */
    createRoleTag: builder.mutation<
      RoleTagSingleResponse,
      { name: string; order?: number }
    >({
      query: (body) => ({
        url: `/api/roles/tags/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["RoleTags"],
    }),

    /**
     * ロールタグ更新（rename / order、仕様書 §5.1.2）。
     */
    updateRoleTag: builder.mutation<
      RoleTagSingleResponse,
      { tagId: number; payload: { name?: string; order?: number } }
    >({
      query: ({ tagId, payload }) => ({
        url: `/api/roles/tags/${tagId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["RoleTags"],
    }),

    /**
     * ロールタグ削除（仕様書 §5.1.3）。`RoleTagAssignment` は cascade で外れるため、
     * Role 側の `tags` 配列も無効化しておく。
     */
    deleteRoleTag: builder.mutation<RoleTagDeleteResponse, number>({
      query: (tagId) => ({
        url: `/api/roles/tags/${tagId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["RoleTags", "Roles"],
    }),

    /**
     * ロールタグの並び替え一括反映（仕様書 §5.1.4）。
     * `ordered_ids` には全 RoleTag ID を漏れなく重複なく含める必要がある。
     */
    reorderRoleTags: builder.mutation<
      RoleTagsListResponse,
      { ordered_ids: number[] }
    >({
      query: ({ ordered_ids }) => ({
        url: `/api/roles/tags/reorder`,
        method: "PATCH",
        body: { ordered_ids },
      }),
      invalidatesTags: ["RoleTags"],
    }),

    getMyRoles: builder.query<MyRolesResponse, void>({
      query: () => `/api/me/roles`,
      providesTags: ["MyRoles"],
    }),

    activateMyRole: builder.mutation<MyRoleToggleResponse, number>({
      query: (roleId) => ({
        url: `/api/me/roles/${roleId}`,
        method: "POST",
      }),
      invalidatesTags: ["MyRoles"],
    }),

    deactivateMyRole: builder.mutation<MyRoleToggleResponse, number>({
      query: (roleId) => ({
        url: `/api/me/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MyRoles"],
    }),

    /**
     * 指定ユーザーのアクティブロール一覧 (`GET /api/users/<user_id>/roles`)
     * オファー作成ウィザードで「送信先ユーザーが担えるロールに②を絞り込む」用途。
     */
    getUserRoles: builder.query<MyRolesResponse, number>({
      query: (userId) => `/api/users/${userId}/roles`,
      providesTags: (_result, _error, userId) => [
        { type: "UserRoles", id: userId },
      ],
    }),

    /**
     * 指定ロールのアクティブユーザー一覧 (`GET /api/roles/<role_id>/users`)
     * オファー作成ウィザードで「ロール先順序の②送信先候補」を取得する用途。
     */
    getRoleUsers: builder.query<RoleUsersResponse, ListRoleUsersParams>({
      query: (params) =>
        `/api/roles/${params.role_id}/users${buildRoleUsersQueryString(params)}`,
      providesTags: (_result, _error, { role_id, following, accepting_offers }) => [
        {
          type: "RoleUsers",
          id: `${role_id}:${following ? 1 : 0}:${accepting_offers ? 1 : 0}`,
        },
      ],
    }),

    /**
     * 他ユーザーへのロール割当（仕様書 §6.5）。writer 権限のみ。
     * 対象に `Creator` 行が無ければ自動作成されるため 409 は発生しない。
     * 201 / 200 ともに成功扱い。
     */
    assignUserToRole: builder.mutation<
      AssignmentToggleResponse,
      { userId: number; roleId: number }
    >({
      query: ({ userId, roleId }) => ({
        url: `/api/users/${userId}/roles/${roleId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        "RoleUsers",
        { type: "UserRoles", id: userId },
      ],
    }),

    /**
     * 他ユーザーからのロール解除（仕様書 §6.5）。冪等。
     */
    unassignUserFromRole: builder.mutation<
      AssignmentToggleResponse,
      { userId: number; roleId: number }
    >({
      query: ({ userId, roleId }) => ({
        url: `/api/users/${userId}/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        "RoleUsers",
        { type: "UserRoles", id: userId },
      ],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRoleTagsQuery,
  useGetRoleDetailQuery,
  useGetRoleOfferPrefillQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useCreateRoleTagMutation,
  useUpdateRoleTagMutation,
  useDeleteRoleTagMutation,
  useReorderRoleTagsMutation,
  useGetMyRolesQuery,
  useActivateMyRoleMutation,
  useDeactivateMyRoleMutation,
  useGetUserRolesQuery,
  useGetRoleUsersQuery,
  useAssignUserToRoleMutation,
  useUnassignUserFromRoleMutation,
} = rolesApi;
